import { Button, Flex, Select, TextField } from "@radix-ui/themes";

import {
  buildDescribedBy,
  type ControlProps,
  isCommitKey,
} from "./LayoutSettingsBasicControls.pres";
import type {
  BreakpointDraft,
  LayoutSettingControl,
} from "./layoutSettings.types";

interface BreakpointDraftInputProps {
  ariaLabel: string;
  ariaDescribedBy: string;
  hasError: boolean;
  disabled: boolean;
  value: string;
  onChange: (value: string) => void;
  onCommit: () => void;
}

const BreakpointDraftInput = ({
  ariaLabel,
  ariaDescribedBy,
  hasError,
  disabled,
  value,
  onChange,
  onCommit,
}: BreakpointDraftInputProps) => (
  <TextField.Root
    size="1"
    variant="soft"
    inputMode="decimal"
    aria-label={ariaLabel}
    aria-describedby={ariaDescribedBy}
    aria-invalid={hasError || undefined}
    disabled={disabled}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => {
      if (isCommitKey(e)) {
        onCommit();
      }
    }}
    onBlur={onCommit}
  />
);

interface BreakpointLayoutPickerProps {
  ariaLabel: string;
  ariaDescribedBy: string;
  disabled: boolean;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  onValueChange: (value: string) => void;
}

const BreakpointLayoutPicker = ({
  ariaLabel,
  ariaDescribedBy,
  disabled,
  value,
  options,
  onValueChange,
}: BreakpointLayoutPickerProps) => (
  <Select.Root value={value} disabled={disabled} onValueChange={onValueChange}>
    <Select.Trigger
      variant="soft"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
    />
    <Select.Content variant="soft">
      {options.map((opt) => (
        <Select.Item key={opt.value} value={opt.value}>
          {opt.label}
        </Select.Item>
      ))}
    </Select.Content>
  </Select.Root>
);

interface BreakpointsControlProps extends ControlProps {
  control: Extract<LayoutSettingControl, { kind: "breakpoints" }>;
}

export const BreakpointsControlPresentation = ({
  field,
  descId,
  errorId,
  control,
  onFieldChange,
}: BreakpointsControlProps) => {
  const fieldId = field.descriptor.id;
  const hasError = !!field.error;
  const describedBy = buildDescribedBy(descId, errorId, hasError);
  const disabled = field.disabled ?? false;
  const rows = Array.isArray(field.value)
    ? (field.value as BreakpointDraft[])
    : [];

  const commitRow = () => onFieldChange(fieldId, { kind: "breakpoint-commit" });
  const changeRow = (
    index: number,
    rowField: "width" | "columns" | "layout",
    value: string,
  ) =>
    onFieldChange(fieldId, {
      kind: "breakpoint-change",
      index,
      field: rowField,
      value,
    });

  return (
    // A generic Flex column does not support aria-invalid either; the numeric
    // row inputs carry it while this container keeps the description link.
    <Flex direction="column" gap="1" aria-describedby={describedBy}>
      {rows.map((row, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: breakpoint rows are positionally ordered
        <Flex align="center" gap="1" key={index}>
          <BreakpointDraftInput
            ariaLabel={`Breakpoint ${index + 1} width`}
            ariaDescribedBy={describedBy}
            hasError={hasError}
            disabled={disabled}
            value={row.width}
            onChange={(v) => changeRow(index, "width", v)}
            onCommit={commitRow}
          />
          <BreakpointDraftInput
            ariaLabel={`Breakpoint ${index + 1} columns`}
            ariaDescribedBy={describedBy}
            hasError={hasError}
            disabled={disabled}
            value={row.columns}
            onChange={(v) => changeRow(index, "columns", v)}
            onCommit={commitRow}
          />
          {/* The layout picker is a discrete control, so it applies at once. */}
          <BreakpointLayoutPicker
            ariaLabel={`Breakpoint ${index + 1} layout`}
            ariaDescribedBy={describedBy}
            disabled={disabled}
            value={row.layout}
            options={control.layoutOptions}
            onValueChange={(v) => changeRow(index, "layout", v)}
          />
          <Button
            type="button"
            size="1"
            variant="soft"
            color="gray"
            aria-label={`Remove breakpoint ${index + 1}`}
            disabled={disabled}
            onClick={() =>
              onFieldChange(fieldId, {
                kind: "breakpoint-remove",
                index,
              })
            }
          >
            Remove
          </Button>
        </Flex>
      ))}
      <Flex>
        <Button
          type="button"
          size="1"
          variant="soft"
          color="gray"
          disabled={disabled}
          onClick={() => onFieldChange(fieldId, { kind: "breakpoint-add" })}
        >
          Add breakpoint
        </Button>
      </Flex>
    </Flex>
  );
};
